import { DEFAULT_SETTINGS, ledgerEvent, recordKey, SettingsSchema, syncedSettings, type Deps, type Settings, type SyncKind } from '@flow/core';
import type { FlowDb } from './db';

export const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

/** This browser profile's id, the same for every account on it. */
export const deviceId = (): string => {
  try {
    const have = localStorage.getItem('flow.device');
    if (have) return have;
    const id = uuid();
    localStorage.setItem('flow.device', id);
    return id;
  } catch {
    return 'unknown-device';
  }
};

/**
 * Every write to a synced table also appends its ledger event (the change queue) and moves the record's head,
 * in the same transaction: a change can't be saved without its event. Debater names are never recorded.
 */
export interface Recorder {
  put(kind: SyncKind, data: unknown): Promise<void>;
  del(kind: SyncKind, recordId: string): Promise<void>;
}

export const createRecorder = (db: FlowDb, accountId: string, onChange: () => void = () => {}): Recorder => {
  const append = async (e: ReturnType<typeof ledgerEvent>) => {
    await db.transaction('rw', db.outbox, db.heads, async () => {
      await db.outbox.add(e);
      await db.heads.put({ key: recordKey(e.kind, e.recordId), at: e.at, id: e.id });
    });
    onChange();
  };
  const ctx = () => ({ accountId, deviceId: deviceId(), now: Date.now(), newId: uuid });
  return {
    put: (kind, data) => append(ledgerEvent(ctx(), kind, { op: 'put', data })),
    del: (kind, recordId) => append(ledgerEvent(ctx(), kind, { op: 'delete', recordId })),
  };
};

/** Without an account (tests of the stores alone): nothing is recorded. */
const noRecorder: Recorder = { put: async () => {}, del: async () => {} };

/** IndexedDB implementations of every store the use cases need. */
export const createDexieDeps = (db: FlowDb, rec: Recorder = noRecorder): Deps => {
  // A write and its ledger event, atomically (joins the caller's transaction when there is one).
  const rw = <T,>(fn: () => Promise<T>) => db.transaction('rw', db.everything, fn);

  return {
    tournaments: {
      get: (id) => db.tournaments.get(id),
      list: () => db.tournaments.orderBy('updatedAt').reverse().toArray(),
      put: (t) => rw(async () => { await db.tournaments.put(t); await rec.put('tournament', t); }),
      delete: (id) => rw(async () => { await db.tournaments.delete(id); await rec.del('tournament', id); }),
    },
    rounds: {
      get: (id) => db.rounds.get(id),
      listByTournament: (tid) => db.rounds.where('tournamentId').equals(tid).toArray(),
      put: (r) => rw(async () => { await db.rounds.put(r); await rec.put('round', r); }),
      delete: (id) => rw(async () => { await db.rounds.delete(id); await rec.del('round', id); }),
    },
    // Names: device-local, never recorded.
    names: {
      get: (id) => db.names.get(id),
      put: async (n) => void (await db.names.put(n)),
      delete: (id) => db.names.delete(id),
      listDueForPurge: (now) => db.names.where('purgeAfter').belowOrEqual(now).toArray(),
    },
    flow: {
      rows: (rid) => db.rows.where('roundId').equals(rid).toArray(),
      cells: (rid) => db.cells.where('roundId').equals(rid).toArray(),
      getCell: (id) => db.cells.get(id),
      putRow: (r) => rw(async () => { await db.rows.put(r); await rec.put('row', r); }),
      putCell: (c) => rw(async () => { await db.cells.put(c); await rec.put('cell', c); }),
      // Cascades record one tombstone per record, so other devices remove exactly what this one did.
      deleteRow: (rowId) =>
        rw(async () => {
          const cellIds = (await db.cells.where('rowId').equals(rowId).primaryKeys()) as string[];
          await db.cells.bulkDelete(cellIds);
          await db.rows.delete(rowId);
          for (const id of cellIds) await rec.del('cell', id);
          await rec.del('row', rowId);
        }),
      deleteRound: (rid) =>
        rw(async () => {
          const cellIds = (await db.cells.where('roundId').equals(rid).primaryKeys()) as string[];
          const rowIds = (await db.rows.where('roundId').equals(rid).primaryKeys()) as string[];
          await db.cells.bulkDelete(cellIds);
          await db.rows.bulkDelete(rowIds);
          for (const id of cellIds) await rec.del('cell', id);
          for (const id of rowIds) await rec.del('row', id);
        }),
    },
    timers: {
      get: (id) => db.timers.get(id),
      put: (t) => rw(async () => { await db.timers.put(t); await rec.put('timer', t); }),
      delete: (id) => rw(async () => { await db.timers.delete(id); await rec.del('timer', id); }),
    },
    ballots: {
      get: (id) => db.ballots.get(id),
      put: (b) => rw(async () => { await db.ballots.put(b); await rec.put('ballot', b); }),
      delete: (id) => rw(async () => { await db.ballots.delete(id); await rec.del('ballot', id); }),
    },
    settings: {
      get: async () => readSettings(db),
      // Only a change to a synced setting is recorded (opening a tournament changes just this device's "last opened").
      put: (s) =>
        rw(async () => {
          const before = syncedSettings(await readSettings(db));
          await db.settings.put(s);
          if (JSON.stringify(before) !== JSON.stringify(syncedSettings(s))) await rec.put('settings', s);
        }),
    },
    backup: {
      exportAll: async () => ({
        tournaments: await db.tournaments.toArray(),
        rounds: await db.rounds.toArray(),
        names: await db.names.toArray(),
        rows: await db.rows.toArray(),
        cells: await db.cells.toArray(),
        timers: await db.timers.toArray(),
        ballots: await db.ballots.toArray(),
      }),
      importAll: (b) =>
        rw(async () => {
          await db.tournaments.bulkPut(b.tournaments);
          await db.rounds.bulkPut(b.rounds);
          await db.names.bulkPut(b.names);
          await db.rows.bulkPut(b.rows);
          await db.cells.bulkPut(b.cells);
          await db.timers.bulkPut(b.timers);
          await db.ballots.bulkPut(b.ballots);
          for (const t of b.tournaments) await rec.put('tournament', t);
          for (const r of b.rounds) await rec.put('round', r);
          for (const r of b.rows) await rec.put('row', r);
          for (const c of b.cells) await rec.put('cell', c);
          for (const t of b.timers) await rec.put('timer', t);
          for (const x of b.ballots) await rec.put('ballot', x);
        }),
    },
    clock: { now: () => Date.now() },
    ids: { next: uuid },
    tx: { run: (fn) => rw(fn) },
  };
};

/** Settings, tolerating older or partial records: defaults merged in, then validated. */
export const readSettings = async (db: FlowDb): Promise<Settings> => {
  const s = (await db.settings.get('settings')) as (Partial<Settings> & { audioAlerts?: boolean }) | undefined;
  // (Old audioAlerts: true meant 30s, 10s and time's up.)
  const legacy = s?.timerAlertsSec === undefined && s?.audioAlerts === true ? { timerAlertsSec: [30, 10, 0] } : {};
  const parsed = SettingsSchema.safeParse({ ...DEFAULT_SETTINGS, ...s, ...legacy });
  return parsed.success ? parsed.data : DEFAULT_SETTINGS;
};
